/**
 * Utilitários para manipulação de datas sem problemas de timezone
 */

import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Formata uma data sem lançar `RangeError: Invalid time value`.
 *
 * Tolerante a:
 *  - `null`/`undefined`/string vazia → retorna fallback
 *  - timestamps malformados ou parsing inválido → retorna fallback
 *  - `Date` ou string ISO ou número (epoch ms)
 *
 * Use sempre que a data vier de fonte externa (API/audit log/Oracle) onde
 * o shape pode variar. Evita crash da árvore React por uma data quebrada.
 *
 * @param value - Date | string | number | null | undefined
 * @param fmt - format pattern do date-fns (ex: `"dd/MM/yyyy 'às' HH:mm"`)
 * @param fallback - texto retornado quando não dá pra formatar (default `"—"`)
 */
export const safeFormatDate = (
  value: unknown,
  fmt: string,
  fallback = "—",
): string => {
  if (value === null || value === undefined || value === "") return fallback;
  const date = value instanceof Date ? value : new Date(value as string | number);
  if (isNaN(date.getTime())) return fallback;
  try {
    return format(date, fmt, { locale: ptBR });
  } catch {
    return fallback;
  }
};

/**
 * Faz parse de uma string de data YYYY-MM-DD para um objeto Date
 * sem problemas de timezone (usa horário local, não UTC)
 *
 * @param dateString - String no formato YYYY-MM-DD
 * @returns Date object no horário local
 *
 * @description
 * Quando você usa new Date("2025-01-12"), o JavaScript interpreta isso como UTC,
 * causando problemas de timezone (aparece um dia antes em timezones negativos como Brasil).
 * Esta função faz o parse manualmente para evitar esse problema.
 */
export const parseDateStringLocal = (dateString: string): Date => {
  if (!dateString) return new Date();
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formata uma string de data YYYY-MM-DD para o formato brasileiro DD/MM/YYYY
 * sem problemas de timezone
 *
 * @param dateString - String no formato YYYY-MM-DD
 * @returns String no formato DD/MM/YYYY ou "---" se inválido
 */
export const formatDateToBR = (dateString?: string): string => {
  if (!dateString) return "---";

  try {
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) return "---";
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
};

/**
 * Compara duas datas no formato YYYY-MM-DD
 * Retorna true se endDate é posterior ou igual a startDate
 *
 * @param startDate - Data de início no formato YYYY-MM-DD
 * @param endDate - Data de fim no formato YYYY-MM-DD
 * @returns boolean
 */
export const isEndDateValid = (startDate: string, endDate: string): boolean => {
  const start = parseDateStringLocal(startDate);
  const end = parseDateStringLocal(endDate);
  return end >= start;
};

/**
 * Compara duas datas no formato YYYY-MM-DD
 * Retorna true se endDate é estritamente posterior a startDate
 *
 * @param startDate - Data de início no formato YYYY-MM-DD
 * @param endDate - Data de fim no formato YYYY-MM-DD
 * @returns boolean
 */
export const isEndDateAfterStartDate = (startDate: string, endDate: string): boolean => {
  const start = parseDateStringLocal(startDate);
  const end = parseDateStringLocal(endDate);
  return end > start;
};
